import { useEffect, useState } from "react";
import { Navigate, Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Navbar } from "@/components/Navbar";
import { NetworkCanvas } from "@/components/NetworkCanvas";
import { Footer } from "@/components/Contact";
import { CaseStudyContent } from "@/components/CaseStudyContent";
import { projects } from "@/data/portfolio";

export function CaseStudyPage() {
  const { projectId } = useParams();
  const project = projects.find((p) => p.id === projectId);
  const [zoomImage, setZoomImage] = useState(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [projectId]);

  if (!project) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="grain relative min-h-screen overflow-x-clip">
      <NetworkCanvas />
      <Navbar />

      <main className="relative z-10 mx-auto max-w-6xl px-5 pb-20 pt-28 sm:px-8 sm:pb-24">
        <div className="mb-8 flex items-center justify-between gap-4">
          <Link
            to="/#work"
            className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.16em] text-muted-foreground transition-colors hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to work
          </Link>
          <span className="font-mono text-[0.68rem] uppercase tracking-[0.2em] text-primary">
            Case study · {project.index}
          </span>
        </div>

        <section className="mx-auto max-w-4xl">
          <span className="kicker">{project.domain}</span>
          <h1 className="mt-4 font-display text-[clamp(2.4rem,6vw,4.75rem)] font-bold leading-[0.96] text-foreground">
            {project.name}
          </h1>
          <p className="mt-4 max-w-3xl text-lg leading-relaxed text-muted-foreground sm:text-xl">
            {project.tagline}
          </p>

          <div className="mt-10">
            <CaseStudyContent project={project} onZoomImage={setZoomImage} />
          </div>
        </section>
      </main>

      <Footer />

      <Dialog open={!!zoomImage} onOpenChange={(o) => !o && setZoomImage(null)}>
        <DialogContent className="max-h-[92vh] w-[min(94vw,72rem)] overflow-hidden border-border bg-background p-0">
          <DialogHeader className="border-b border-border px-6 py-5 text-left sm:px-8 sm:py-6">
            <div className="font-mono text-[0.68rem] uppercase tracking-[0.2em] text-primary">
              {zoomImage?.title || "Zoom view"}
            </div>
            <DialogTitle className="font-display text-2xl font-bold text-foreground">{project.name}</DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              {zoomImage?.description || "Zoomed view"}
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-auto p-4 sm:p-8">
            <img
              src={zoomImage?.src}
              alt={zoomImage?.alt || ""}
              className="mx-auto h-auto w-full max-w-none rounded-2xl border border-border bg-background"
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
